import SocketContext from "../../../context/socket-context";
import {
  useState,
  useEffect,
  useContext,
  useMemo,
  SetStateAction,
  Dispatch,
} from "react";
import { useNavigate } from "react-router";
import {
  SessionStatus,
  SessionTempo,
  Stored,
  StudentOpinion,
  PopulatedVotingSession,
} from "../../../types";

import StudentWaitingScreen from "../../../components/student/waiting_screen";
import StatementComponent from "../../../components/student/statement";
import Controls from "../../../components/teacher/controls";

export default function Session({
  votingSession,
  status,
  answers,
  setAnswers,
}: {
  votingSession: PopulatedVotingSession;
  status: SessionStatus;
  answers: Array<StudentOpinion>;
  setAnswers: Dispatch<SetStateAction<Array<Stored<StudentOpinion>>>>;
}) {
  const socket = useContext(SocketContext);
  const [currentIndex, setCurrentIndex] = useState<number>(
    votingSession.currentStatementIndex,
  );
  const navigate = useNavigate();
  const currentStatement = useMemo(
    () => votingSession.statements[currentIndex],
    [votingSession, currentIndex],
  );
  const currentAnswered = useMemo(
    () =>
      answers.findIndex((a) => a.statementId === currentStatement?._id) !== -1,
    [currentStatement, answers],
  );

  useEffect(() => {
    socket.on("updateCurrentIndex", setCurrentIndex);

    return () => {
      socket.off("updateCurrentIndex", setCurrentIndex);
    };
  }, [votingSession]);

  useEffect(() => {
    if (status === SessionStatus.Results) {
      navigate("results");
    }
  }, [status]);

  function handleStatementAnswered(answer: Stored<StudentOpinion>) {
    setAnswers((arr) => [...arr, answer]);

    if (votingSession.tempo === SessionTempo.Student) {
      setCurrentIndex((i) => i + 1);
    }
  }

  if (
    status === SessionStatus.Draft ||
    currentAnswered ||
    currentStatement === undefined
  ) {
    return <StudentWaitingScreen status={status} />;
  }

  return (
    <>
      <StatementComponent
        statement={currentStatement}
        session={votingSession}
        onStatementAnswered={handleStatementAnswered}
        // We force the component to fully re-render when the argument
        key={currentStatement._id}
      />
      <div className="voting-session__controls">
        <Controls
          status={status}
          currentStatementIndex={currentIndex}
          statementsCount={votingSession.statements.length}
        />
      </div>
    </>
  );
}
